/**
 * GridNode - 2D array pattern editor
 *
 * Stores and edits a 2D grid of numbers (columns x rows).
 * UI interpretation is handled by the view layer.
 *
 * Endpoints:
 * - pattern (output): the 2D array
 * - width, height (input/output): grid dimensions
 * - outputMin, outputMax (input/output): value range
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { params } from '@/nodes/TypedEndpoints';

const endpoints = params({
  pattern: { dataType: 'array', isOutput: true },
  width: {  isOutput: true, value: 16, min: 1, max: 128, step: 1 },
  height: {  isOutput: true, value: 8, min: 1, max: 128, step: 1 },
  outputMin: {  isOutput: true, value: 0, min: 0, max: 127 },
  outputMax: {  isOutput: true, value: 127, min: 0, max: 127 },
});

export class GridNode extends BaseNode<typeof endpoints> {
  private highlightStep = -1;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    // Initialize grid to match width/height
    const width = this.params.width.value;
    const height = this.params.height.value;
    const pattern = this.params.pattern.value;

    if (!pattern || pattern.length !== width || (pattern[0]?.length ?? 0) !== height) {
      this.params.pattern.set(this.createEmptyGrid(width, height));
    }
    this.emitPattern();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'width':
        this.resize(payload.value, this.height);
        break;
      case 'height':
        this.resize(this.width, payload.value);
        break;
      case 'pattern':
        this.emitPattern();
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Accessors
  // ─────────────────────────────────────────────────────────────

  get pattern(): number[][] {
    return this.params.pattern.value;
  }

  set pattern(value: number[][]) {
    this.params.pattern.set(value);
  }

  get width(): number {
    return this.params.width.value;
  }

  get height(): number {
    return this.params.height.value;
  }

  get outputRange(): { min: number; max: number } {
    return {
      min: this.params.outputMin.value,
      max: this.params.outputMax.value
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Grid Operations
  // ─────────────────────────────────────────────────────────────

  getCell(x: number, y: number): number {
    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      return this.pattern[x]?.[y] ?? 0;
    }
    return 0;
  }

  setCell(x: number, y: number, value: number): void {
    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      const clamped = this.clampValue(value);
      const newPattern = this.pattern.map(col => [...col]);
      newPattern[x][y] = clamped;
      this.pattern = newPattern;
    }
  }

  toggleCell(x: number, y: number): void {
    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      const current = this.pattern[x][y];
      const newValue = current === 0 ? this.outputRange.max : 0;
      this.setCell(x, y, newValue);
    }
  }

  getColumn(x: number): number[] {
    if (x >= 0 && x < this.width) {
      return [...(this.pattern[x] ?? [])];
    }
    return [];
  }

  setColumn(x: number, values: number[]): void {
    if (x >= 0 && x < this.width) {
      const clamped = values.map(v => this.clampValue(v)).slice(0, this.height);
      const newPattern = this.pattern.map(col => [...col]);
      newPattern[x] = clamped;
      this.pattern = newPattern;
    }
  }

  resize(width: number, height: number): void {
    if (width < 1 || height < 1) return;
    if (width === this.width && height === this.height) return;

    const newPattern = this.createEmptyGrid(width, height);
    const copyWidth = Math.min(width, this.width);
    const copyHeight = Math.min(height, this.height);

    for (let x = 0; x < copyWidth; x++) {
      for (let y = 0; y < copyHeight; y++) {
        newPattern[x][y] = this.pattern[x]?.[y] ?? 0;
      }
    }

    this.params.width.set(width);
    this.params.height.set(height);
    this.params.pattern.set(newPattern);
  }

  clear(): void {
    this.pattern = this.createEmptyGrid(this.width, this.height);
  }

  // ─────────────────────────────────────────────────────────────
  // Animation
  // ─────────────────────────────────────────────────────────────

  setHighlightStep(step: number): void {
    this.highlightStep = step % this.width;
  }

  get currentHighlightStep(): number {
    return this.highlightStep;
  }

  // ─────────────────────────────────────────────────────────────
  // Private
  // ─────────────────────────────────────────────────────────────

  private createEmptyGrid(width: number, height: number): number[][] {
    return Array.from({ length: width }, () => Array(height).fill(0));
  }

  private clampValue(value: number): number {
    return Math.max(this.outputRange.min, Math.min(this.outputRange.max, value));
  }

  private emitPattern(): void {
    this.params.pattern.set(this.pattern);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const gridNodeDef: NodeDef<GridNode> = {
  type: 'Grid',
  info: '2D array pattern editor. Stores a grid of numbers with configurable dimensions and value range.',
  domain: Domain.Data,
  endpoints,
  ctor: GridNode
};
